import { z } from 'zod';

const multiplierItemSchema = z
  .object({
    name: z.string(),
    code: z.string().optional(),
    defaultMultiplier: z.number().nullable().optional(),
    defaultPerShiftThresholdHours: z.union([z.string(), z.number()]).nullable().optional(),
    defaultPerWeekThresholdHours: z.union([z.string(), z.number()]).nullable().optional(),
    perShiftBillingStructures: z.array(z.string()).nullable().optional(),
    perWeekBillingStructures: z.array(z.string()).nullable().optional(),
  })
  .passthrough();

export const multipliersSchema = z
  .object({
    rateMultipliers: z.array(multiplierItemSchema).optional(),
    weekStart: z.union([z.string(), z.number()]).nullable().optional(),
    billingStructureConfig: z
      .object({
        weekStart: z.union([z.string(), z.number()]).nullable().optional(),
      })
      .passthrough()
      .nullable()
      .optional(),
    roles: z
      .array(
        z
          .object({
            billingStructure: z.enum(['hourly', 'day_rate']).optional(),
          })
          .passthrough()
      )
      .optional(),
  })
  .passthrough()
  .superRefine((data, ctx) => {
    const multipliers = data.rateMultipliers;
    const roles = data.roles;

    const hasDayRateRole = roles?.some((r) => r.billingStructure === 'day_rate') ?? false;
    const hasHourlyRole = roles?.some((r) => r.billingStructure === 'hourly') ?? false;

    // --- Array-level checks ---
    if (!multipliers?.length) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateMultipliers'],
        message: 'Rate multipliers are required',
      });
      return;
    }

    const hasStraightTime = multipliers.some(
      (m) => m.code === 'STRAIGHT_TIME' && m.defaultMultiplier === 1
    );
    if (!hasStraightTime) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['rateMultipliers'],
        message: 'Straight time rate multiplier (1.0x) is required',
      });
    }

    // --- Per-multiplier checks ---
    multipliers.forEach((multiplier, index) => {
      // Duplicate name check (case-insensitive)
      const nameExists = multipliers.some(
        (m, i) =>
          i !== index &&
          m.name.trim().toLowerCase() === multiplier.name.trim().toLowerCase()
      );
      if (nameExists) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`rateMultipliers[${index}].name`],
          message: 'Multiplier with this name already exists',
        });
      }

      if (!multiplier.name.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [`rateMultipliers[${index}].name`],
          message: 'Multiplier name is required',
        });
      }

      if (multiplier.code === 'STRAIGHT_TIME') {
        // STRAIGHT_TIME-specific rules
        if (multiplier.defaultMultiplier !== 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rateMultipliers[${index}].defaultMultiplier`],
            message: 'Straight time multiplier rate must be 1.0',
          });
        }
        if (
          (multiplier.defaultPerShiftThresholdHours !== 0 &&
            multiplier.defaultPerShiftThresholdHours !== null) ||
          (multiplier.defaultPerWeekThresholdHours !== 0 &&
            multiplier.defaultPerWeekThresholdHours !== null)
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rateMultipliers[${index}].defaultPerShiftThresholdHours`],
            message: 'Straight time threshold must be 0',
          });
        }
      } else {
        // Non-STRAIGHT_TIME rules
        if (multiplier.defaultMultiplier == null || multiplier.defaultMultiplier <= 1) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rateMultipliers[${index}].defaultMultiplier`],
            message: 'Multiplier rate must be greater than 1.0',
          });
        }

        const rawShift = multiplier.defaultPerShiftThresholdHours;
        const rawWeek = multiplier.defaultPerWeekThresholdHours;
        const hasDayRateThreshold = rawShift !== null && rawShift !== 0;
        const hasWeeklyThreshold = rawWeek !== null && rawWeek !== 0;

        if (!hasDayRateThreshold && !hasWeeklyThreshold) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rateMultipliers[${index}].defaultPerShiftThresholdHours`],
            message: `"${multiplier.name}" needs at least one threshold to take effect`,
          });
        }

        // Day-rate threshold bounds
        if (hasDayRateRole && rawShift !== null) {
          const h = typeof rawShift === 'string' ? parseFloat(rawShift) : (rawShift ?? 0);
          if (isNaN(h) || h <= 0 || h >= 72) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`rateMultipliers[${index}].defaultPerShiftThresholdHours`],
              message: 'Day rate threshold must be > 0 and < 72 hours',
            });
          }
        }

        // Weekly threshold bounds
        if (hasHourlyRole && rawWeek !== null) {
          const h = typeof rawWeek === 'string' ? parseFloat(rawWeek) : (rawWeek ?? 0);
          if (isNaN(h) || h <= 0 || h > 168) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`rateMultipliers[${index}].defaultPerWeekThresholdHours`],
              message: 'Weekly threshold must be > 0 and ≤ 168 hours',
            });
          }
        }
      }

      // Billing structure applicability requirements
      if (multiplier.defaultPerShiftThresholdHours !== null) {
        if (
          !multiplier.perShiftBillingStructures ||
          multiplier.perShiftBillingStructures.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rateMultipliers[${index}].perShiftBillingStructures`],
            message: `Select applicability for "${multiplier.name}" per-shift threshold.`,
          });
        }
      }
      if (multiplier.defaultPerWeekThresholdHours !== null) {
        if (
          !multiplier.perWeekBillingStructures ||
          multiplier.perWeekBillingStructures.length === 0
        ) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [`rateMultipliers[${index}].perWeekBillingStructures`],
            message: `Select applicability for "${multiplier.name}" weekly threshold.`,
          });
        }
      }
    });

    // --- Cross-multiplier duplicate threshold checks ---

    // Duplicate day-rate thresholds
    if (hasDayRateRole) {
      const dayRateThresholds = new Map<number, number[]>();
      multipliers.forEach((multiplier, index) => {
        if (multiplier.defaultPerShiftThresholdHours != null) {
          const raw = multiplier.defaultPerShiftThresholdHours;
          const h = typeof raw === 'string' ? parseFloat(raw) : (raw ?? 0);
          if (!isNaN(h)) {
            if (!dayRateThresholds.has(h)) dayRateThresholds.set(h, []);
            dayRateThresholds.get(h)!.push(index);
          }
        }
      });
      dayRateThresholds.forEach((indices, threshold) => {
        if (indices.length > 1) {
          indices.forEach((index) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`rateMultipliers[${index}].defaultPerShiftThresholdHours`],
              message: `Day rate threshold ${threshold} hours is already used by another multiplier`,
            });
          });
        }
      });
    }

    // Duplicate weekly thresholds
    if (hasHourlyRole) {
      const weeklyThresholds = new Map<number, number[]>();
      multipliers.forEach((multiplier, index) => {
        if (multiplier.defaultPerWeekThresholdHours != null) {
          const raw = multiplier.defaultPerWeekThresholdHours;
          const h = typeof raw === 'string' ? parseFloat(raw) : (raw ?? 0);
          if (!isNaN(h)) {
            if (!weeklyThresholds.has(h)) weeklyThresholds.set(h, []);
            weeklyThresholds.get(h)!.push(index);
          }
        }
      });
      weeklyThresholds.forEach((indices, threshold) => {
        if (indices.length > 1) {
          indices.forEach((index) => {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: [`rateMultipliers[${index}].defaultPerWeekThresholdHours`],
              message: `Weekly threshold ${threshold} hours is already used by another multiplier`,
            });
          });
        }
      });
    }

    // --- weekStart requirement ---
    const usesWeeklyThreshold =
      hasHourlyRole &&
      multipliers.some((m) => {
        if (m.defaultPerWeekThresholdHours == null) return false;
        const h =
          typeof m.defaultPerWeekThresholdHours === 'string'
            ? parseFloat(m.defaultPerWeekThresholdHours)
            : m.defaultPerWeekThresholdHours;
        return !isNaN(h) && h > 0;
      });

    if (usesWeeklyThreshold) {
      const weekStart =
        data.billingStructureConfig?.weekStart ??
        (data as Record<string, unknown>).weekStart ??
        null;
      if (weekStart === null || weekStart === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['weekStart'],
          message: 'Work week start day is required for weekly thresholds.',
        });
      }
    }
  });

export type MultipliersSchemaData = z.infer<typeof multipliersSchema>;
